import { getSocialPlatformSettings } from "@/fixtures/socialPlatforms";
import { Avatar } from "antd";
import React, { useMemo } from "react";
import { FaRegComment } from "react-icons/fa";
import {
  FiChevronDown,
  FiHeart,
  FiMoreHorizontal,
  FiUpload,
  FiUser,
} from "react-icons/fi";
import CaptionExpand from "./components/CaptionExpand";
import MediaSection from "./components/MediaSection";
import { BaseSocialCardProps } from "./types";

export interface PinterestCardProps extends BaseSocialCardProps {
  title?: string;
  link?: string;
  platformId: string;
  category?: string;
  likes?: number;
}

const PinterestCard: React.FC<PinterestCardProps> = ({
  caption = "",
  mediaUrls = [],
  username = "User",
  profileUrl,
  title = "Sample Pin Title",
  link = "sample.com",
  category = "Category",
  likes = 48,
  platformId,
  isTemplateVideo,
}) => {
  const captionLimit = useMemo(() => {
    const settings = getSocialPlatformSettings(platformId);
    return settings?.limits?.caption;
  }, [platformId]);

  const displayCaption = useMemo(() => {
    if (captionLimit && caption.length > captionLimit) {
      const effectiveLimit = Math.min(captionLimit, 60);
      return caption.substring(0, effectiveLimit) + "...";
    }
    return caption;
  }, [caption, captionLimit]);

  return (
    <div className="w-full mx-auto  bg-antd-colorBgContainer text-antd-colorText rounded-xl overflow-hidden shadow-lg font-sans border border-antd-colorBorderSecondary">
      <style>{`
        .pinterest-card-preview .hashtag {
          color: #0095F6; /* Using Instagram's Blue */
          text-decoration: none;
        }
        .pinterest-card-preview .hashtag:hover {
          text-decoration: underline;
        }
      `}</style>
      <div className="px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-4 text-antd-colorTextSecondary">
          <div className="flex items-center gap-1">
            <FiHeart className="w-5 h-5" />
            <span className="text-sm font-semibold">{likes}</span>
          </div>
          <FaRegComment className="w-5 h-5" />
          <FiUpload className="w-5 h-5" />
          <FiMoreHorizontal className="w-5 h-5" />
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1 text-sm font-semibold text-antd-colorText px-2 py-1 rounded">
            {category}
            <FiChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="font-medium rounded-xl px-3 py-2 text-white text-sm bg-[#DC0019]"
          >
            Save
          </button>
        </div>
      </div>

      <div className="relative group px-3 pt-0 pb-2 min-h-[200px]">
        <MediaSection mediaUrls={mediaUrls} isTemplateVideo={isTemplateVideo} />
      </div>

      <div className="px-4 pt-2 pb-4">
        <div className="flex justify-between items-start gap-2 mb-3">
          {/* Title and Caption Block */}
          <div className="flex-grow">
            <h2 className="font-bold text-lg mb-1 leading-tight">{title}</h2>
            {displayCaption && (
              <CaptionExpand
                text={displayCaption}
                initialLines={1}
                showMoreText="...more"
                showLessText="...less"
                linkColor="gray"
                className="text-sm  font-normal text-antd-colorTextSecondary mb-1"
                buttonBackgroundClass="bg-antd-colorBgContainer"
              />
            )}
          </div>

          {/* Visit Site Button */}
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-antd-colorFillSecondary hover:bg-antd-colorFillTertiary text-antd-colorText text-sm font-medium py-1.5 px-3 rounded-md flex-shrink-0"
            >
              Visit site
            </a>
          )}
        </div>

        <div className="flex items-center gap-2 mt-2">
          <Avatar
            size={24}
            src={profileUrl}
            icon={!profileUrl ? <FiUser className="w-4 h-4" /> : undefined}
          />
          <span className="font-semibold text-xs">{username}</span>
        </div>
      </div>
    </div>
  );
};

export default PinterestCard;
